import { useState, useEffect } from 'react';
import { api } from '../api/client';

interface VaultProfile {
  working_style: string;
  career_core: string;
  career_roadmap: string;
  privacy_guard: string;
  strengths: string;
  current_focus: string;
}

const DEFAULT_PROFILE: VaultProfile = {
  working_style: '',
  career_core: '',
  career_roadmap: '',
  privacy_guard: '',
  strengths: '',
  current_focus: '',
};

export function ProfilePage() {
  const [profile, setProfile] = useState<VaultProfile>(DEFAULT_PROFILE);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [activeSection, setActiveSection] = useState<keyof VaultProfile>('working_style');

  useEffect(() => {
    setLoading(true);
    api.getVaultProfile()
      .then(data => setProfile({ ...DEFAULT_PROFILE, ...data }))
      .catch(() => {/* not set yet, use defaults */})
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    try {
      await api.saveVaultProfile(profile);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e: any) {
      alert('Lưu thất bại: ' + (e.message ?? 'Lỗi không xác định'));
    } finally {
      setSaving(false);
    }
  };

  const sections: { key: keyof VaultProfile; label: string; icon: string; placeholder: string; hint: string }[] = [
    {
      key: 'working_style',
      label: 'Phong Cách Làm Việc',
      icon: '⚡',
      placeholder: 'VD: Tôi làm việc hiệu quả nhất vào buổi sáng, ưa phân tích trước hành động, thích tài liệu ngắn gọn súc tích...',
      hint: 'AI sẽ dùng thông tin này để điều chỉnh cách giao tiếp và phong cách trả lời cho phù hợp với bạn.',
    },
    {
      key: 'career_core',
      label: 'Bản Sắc Nghề Nghiệp',
      icon: '🎯',
      placeholder: 'VD: Senior Product Manager tại fintech, 7 năm kinh nghiệm, chuyên về growth và data-driven decision...',
      hint: 'Giúp AI hiểu background chuyên môn để đưa ra tư vấn phù hợp với ngành và mức độ của bạn.',
    },
    {
      key: 'strengths',
      label: 'Điểm Mạnh & Kỹ Năng',
      icon: '💪',
      placeholder: 'VD: Tư duy hệ thống, thuyết trình, SQL, Python cơ bản, quản lý stakeholder...',
      hint: 'AI sẽ phát huy điểm mạnh này trong các gợi ý và giải pháp.',
    },
    {
      key: 'career_roadmap',
      label: 'Lộ Trình Phát Triển',
      icon: '🗺️',
      placeholder: 'VD: Mục tiêu 2025: lên Director PM. Cần học thêm AI/ML basics và leadership. Đang theo dõi các công ty SaaS B2B...',
      hint: 'AI sẽ ưu tiên gợi ý tài liệu và hành động phù hợp với mục tiêu dài hạn của bạn.',
    },
    {
      key: 'current_focus',
      label: 'Trọng Tâm Hiện Tại',
      icon: '🔥',
      placeholder: 'VD: Q4 2025: Launch tính năng AI analytics, build team 3 PM mới, hoàn thiện OKR framework cho team...',
      hint: 'Giúp AI tập trung vào những gì đang quan trọng nhất với bạn trong giai đoạn này.',
    },
    {
      key: 'privacy_guard',
      label: 'Privacy Guard',
      icon: '🛡️',
      placeholder: 'VD: Không chia sẻ tên công ty, dự án nội bộ, số liệu doanh thu, thông tin đối tác...',
      hint: 'AI sẽ tự động ẩn/redact các thông tin này trước khi xử lý hoặc chia sẻ với hệ thống bên ngoài.',
    },
  ];

  const activeSection_data = sections.find(s => s.key === activeSection)!;

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', gap: '16px', flexDirection: 'column' }}>
        <div className="spinner" style={{ width: 32, height: 32 }} />
        <span style={{ color: 'var(--text-muted)', fontSize: '13px' }}>Đang tải Vault Profile...</span>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', gap: '0' }}>

      {/* Page Header */}
      <div className="page-header" style={{ flexShrink: 0 }}>
        <div>
          <h1 className="page-title">👤 Vault Profile — Bản Sắc Cá Nhân</h1>
          <p className="page-subtitle">
            Hồ sơ này giúp AI hiểu sâu về bạn — phong cách, mục tiêu, điểm mạnh — để cá nhân hóa mọi tương tác
          </p>
        </div>
        <button
          className="btn btn--primary"
          onClick={handleSave}
          disabled={saving}
          style={{ minWidth: 120 }}
        >
          {saving ? <span className="spinner" style={{ width: 14, height: 14 }} /> : saved ? '✅ Đã lưu!' : '💾 Lưu Profile'}
        </button>
      </div>

      {/* Body */}
      <div style={{ display: 'flex', flex: 1, gap: '20px', overflow: 'hidden' }}>

        {/* Left: Section tabs */}
        <div style={{
          width: 220,
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: '4px',
          paddingTop: '4px',
        }}>
          {sections.map(s => (
            <button
              key={s.key}
              onClick={() => setActiveSection(s.key)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                padding: '10px 14px',
                borderRadius: '10px',
                border: 'none',
                background: activeSection === s.key ? 'rgba(124,58,237,0.18)' : 'transparent',
                color: activeSection === s.key ? '#c084fc' : 'var(--text-secondary)',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: activeSection === s.key ? 600 : 400,
                textAlign: 'left',
                transition: 'all 0.2s',
                borderLeft: `3px solid ${activeSection === s.key ? '#a855f7' : 'transparent'}`,
              }}
            >
              <span style={{ fontSize: '16px' }}>{s.icon}</span>
              <span>{s.label}</span>
              {profile[s.key] && (
                <span style={{
                  marginLeft: 'auto',
                  width: 7, height: 7,
                  borderRadius: '50%',
                  background: 'var(--accent-green)',
                  flexShrink: 0,
                }} />
              )}
            </button>
          ))}

          {/* Completion meter */}
          <div style={{
            marginTop: 'auto',
            padding: '16px',
            background: 'var(--bg-glass)',
            border: '1px solid var(--border)',
            borderRadius: '12px',
          }}>
            <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '8px' }}>Độ hoàn thiện profile</div>
            <div style={{ background: 'rgba(255,255,255,0.06)', borderRadius: '6px', height: 8 }}>
              <div style={{
                height: 8,
                borderRadius: '6px',
                background: 'var(--gradient-main)',
                width: `${Math.round((Object.values(profile).filter(v => v.trim()).length / sections.length) * 100)}%`,
                transition: 'width 0.4s',
              }} />
            </div>
            <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '6px', textAlign: 'right' }}>
              {Object.values(profile).filter(v => v.trim()).length}/{sections.length} mục
            </div>
          </div>
        </div>

        {/* Right: Active section editor */}
        <div style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          overflow: 'auto',
        }}>
          <div className="card" style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '12px' }}>

            {/* Section header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', paddingBottom: '12px', borderBottom: '1px solid var(--border)' }}>
              <span style={{ fontSize: '28px' }}>{activeSection_data.icon}</span>
              <div>
                <div style={{ fontSize: '16px', fontWeight: 700 }}>{activeSection_data.label}</div>
                <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px', lineHeight: 1.5 }}>
                  {activeSection_data.hint}
                </div>
              </div>
            </div>

            {/* Textarea */}
            <textarea
              className="textarea"
              value={profile[activeSection]}
              onChange={e => setProfile(prev => ({ ...prev, [activeSection]: e.target.value }))}
              placeholder={activeSection_data.placeholder}
              style={{
                flex: 1,
                minHeight: 200,
                resize: 'vertical',
                fontSize: '14px',
                lineHeight: 1.7,
                padding: '16px',
                background: 'var(--bg-glass)',
                border: '1px solid var(--border)',
                borderRadius: '10px',
              }}
            />

            {/* Tips */}
            <div style={{
              background: 'rgba(124,58,237,0.06)',
              border: '1px solid rgba(124,58,237,0.2)',
              borderRadius: '10px',
              padding: '12px 16px',
              fontSize: '12.5px',
              color: 'var(--text-secondary)',
              lineHeight: 1.6,
            }}>
              💡 <strong>Tips:</strong> Càng chi tiết, AI càng hiểu bạn. Không cần format chuẩn — viết tự nhiên như nhật ký cá nhân. AI sẽ tự học từ ngữ cảnh bạn cung cấp.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
